import { createMemberManagementService } from './appService';
import { createBrowserCloudAuthTokenStore, createCloudAuthSliceService } from './cloudAuthSliceService';
import { createCloudMemberManagementService } from './cloudMemberManagementService';
import {
  createLocalAuthAdapter,
  createLocalInvitationCodeGenerator,
  createLocalJsonStore,
  createLocalSessionAdapter
} from './localAdapters';
import { assertCurrentMemberManagementDataVersion, createEmptyMemberManagementData } from './model';
import { mountMemberManagementReactApp } from './reactApp';
import type { MemberManagementReactAppHandle } from './reactApp';
import type { MemberManagementService } from './appService';

const DATA_KEY = 'choir-nest:member:data';
const AUTH_KEY = 'choir-nest:member:auth';
const SESSION_KEY = 'choir-nest:member:session';
const CLOUD_SESSION_TOKEN_KEY = 'choir-nest:member:cloud-session-token';

export type MemberBackendConfig =
  | { readonly mode: 'local' }
  | { readonly mode: 'cloud-auth-slice'; readonly apiBaseUrl: string }
  | { readonly mode: 'cloud-member-management'; readonly apiBaseUrl: string };

export function resolveMemberBackendConfig(env: Partial<ImportMetaEnv>): MemberBackendConfig {
  const backend = env.VITE_MEMBER_BACKEND;

  if (backend === undefined || backend === 'local') {
    return { mode: 'local' };
  }

  if (backend !== 'cloud-auth-slice' && backend !== 'cloud-member-management') {
    throw new Error(`Unsupported VITE_MEMBER_BACKEND: ${backend}`);
  }

  const apiBaseUrl = env.VITE_API_BASE_URL?.trim();

  if (!apiBaseUrl) {
    throw new Error(
      backend === 'cloud-auth-slice'
        ? 'Missing VITE_API_BASE_URL for cloud auth slice'
        : 'Missing VITE_API_BASE_URL for cloud member management'
    );
  }

  return {
    mode: backend,
    apiBaseUrl
  };
}

export function createMemberManagementBootstrap() {
  return {
    mount: mountConfiguredMemberManagementReactApp
  };
}

export function mountConfiguredMemberManagementReactApp(target: HTMLElement): MemberManagementReactAppHandle {
  const service = createConfiguredMemberManagementService(resolveMemberBackendConfig(import.meta.env));

  return mountMemberManagementReactApp(target, {
    service,
    resetLocalMvpData: async () => {
      localStorage.removeItem(DATA_KEY);
      localStorage.removeItem(AUTH_KEY);
      localStorage.removeItem(SESSION_KEY);
    }
  });
}

function createConfiguredMemberManagementService(config: MemberBackendConfig): MemberManagementService {
  if (config.mode === 'cloud-auth-slice') {
    return createCloudAuthSliceService({
      apiBaseUrl: config.apiBaseUrl,
      fetch: window.fetch.bind(window),
      tokenStore: createBrowserCloudAuthTokenStore(CLOUD_SESSION_TOKEN_KEY)
    });
  }

  if (config.mode === 'cloud-member-management') {
    return createCloudMemberManagementService({
      apiBaseUrl: config.apiBaseUrl,
      fetch: window.fetch.bind(window),
      tokenStore: createBrowserCloudAuthTokenStore(CLOUD_SESSION_TOKEN_KEY)
    });
  }

  return createLocalMemberManagementService();
}

function createLocalMemberManagementService(): MemberManagementService {
  return createMemberManagementService({
    dataStore: createLocalJsonStore(
      DATA_KEY,
      createEmptyMemberManagementData,
      assertCurrentMemberManagementDataVersion
    ),
    auth: createLocalAuthAdapter(AUTH_KEY),
    session: createLocalSessionAdapter(SESSION_KEY),
    invitationCodeGenerator: createLocalInvitationCodeGenerator(),
    ports: {
      now: () => new Date().toISOString(),
      newId: (prefix) => `${prefix}_${crypto.randomUUID()}`
    }
  });
}
